import { FunctionComponent, useEffect, useState } from "react";
import Form from "@rjsf/react-bootstrap";
import validator from "@rjsf/validator-ajv8";
import { ErrorSchema, FormValidation } from "@rjsf/utils";

import { FormSchemaDto } from "../model/FormSchemaDto";
import { Limit } from "../model/Limit";
import { fetchLimits, fetchSchema, postRegistration, RegistrationResult } from "./registrationFormApi";
import { FormSchemaHelper } from "../helper/FormSchemaHelper";
import { LimitContext } from "../context/LimitContext";
import { WaitlistContext, WaitlistState } from "../context/WaitlistContext";
import { isZipCodeBlocked } from "../helper/ZipCodeHelper";
import { DEFAULT_WAITLIST_HINT, DEFAULT_WAITLIST_ZIP_CODE_MESSAGE } from "../helper/WaitlistTexts";
import { Widgets } from "../widget/Widgets";
import { Fields } from "../field/Fields";
import { ExtraErrorHelper } from "../helper/ExtraErrorHelper";
import { CentredSpinner } from "./CentredSpinner";

const toSuccessMessage = (result?: RegistrationResult): string => {
  if (!result || result.waitlisted.length === 0) {
    return "Vielen Dank, die Registrierung wurde erfolgreich gesendet.";
  }
  if (result.registered.length === 0) {
    return `Vielen Dank, Ihr Kind ist auf der Warteliste eingetragen (${result.waitlisted.join(", ")}). Wir melden uns, sobald ein Platz frei ist.`;
  }
  return `Vielen Dank, die Anmeldung wurde gesendet. Angemeldet für: ${result.registered.join(", ")}. Auf der Warteliste für: ${result.waitlisted.join(", ")}.`;
};

export interface RegistrationFormProps {
  limitsEndpoint: string;
  registrationEndpoint: string;
  schemaEndpoint: string;
}

export const RegistrationForm: FunctionComponent<RegistrationFormProps> = ({
  limitsEndpoint,
  registrationEndpoint,
  schemaEndpoint
}) => {
  const [schema, setSchema] = useState<FormSchemaDto>();
  const [limits, setLimits] = useState<Limit[]>([]);

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [extraErrors, setExtraErrors] = useState<ErrorSchema>();

  useEffect(() => {
    fetchSchemaOrSetError();
    fetchLimitsOrSetError();
  }, [limitsEndpoint, schemaEndpoint, registrationEndpoint]);

  if (!schema) {
    return <CentredSpinner />;
  }

  if (!FormSchemaHelper.isFormReleased(schema.formSchema)) {
    const releaseDate = FormSchemaHelper.getReleaseDateWithoutTimeZoning(schema.formSchema);
    const formattedReleaseDate = releaseDate.toLocaleDateString();
    const formattedReleaseTime = releaseDate.toLocaleTimeString();
    return (
      <div className="cointainer">
        <div className="m-5 text-center alert alert-warning">
          <p>
            Die Anmeldung ist aktuell geschlossen und wird voraussichtlich am {formattedReleaseDate},{" "}
            {formattedReleaseTime} aufgeschaltet.
            <br /> Wir freuen uns auf Ihre Anmeldung.
          </p>
        </div>
      </div>
    );
  }

  const options = schema.options;
  const waitlistEnabled = !!options?.waitlistEnabled;
  const waitlist: WaitlistState = {
    enabled: waitlistEnabled,
    allToWaitlist: waitlistEnabled && isZipCodeBlocked(schema.uiSchema, formData),
    zipCodeMessage: options?.waitlistZipCodeMessage || DEFAULT_WAITLIST_ZIP_CODE_MESSAGE
  };
  // mirrors the backend: booked out offers, or every offer for a zip code outside the whitelist
  const waitlistedOffers = waitlistEnabled
    ? limits
        .filter((limit) => formData[limit.property] === true && (waitlist.allToWaitlist || limit.available <= 0))
        .map((limit) => limit.property)
    : [];
  const showWaitlistHint = waitlistedOffers.length > 0 || waitlist.allToWaitlist;

  return (
    <div>
      <noscript>
        Sie müssen Javascript zulassen, um das Anmeldeformular zu verwenden. Bitte nehmen Sie diese Änderungen in den
        Sicherheitseinstellungen Ihres Webbrowsers vor. Ansonsten verwenden Sie einen modernen Webbrowser wie Google
        Chrome oder Mozilla Firefox.
      </noscript>
      <LimitContext.Provider value={limits}>
        <WaitlistContext.Provider value={waitlist}>
          <Form
            className={"registration-form"}
            formData={formData}
            schema={schema.formSchema}
            uiSchema={schema.uiSchema}
            onChange={(e: any) => setFormData(e.formData)}
            onSubmit={(e: any) => submitForm(e.formData)}
            widgets={Widgets}
            fields={Fields}
            extraErrors={extraErrors}
            transformErrors={ExtraErrorHelper.sanitizeErrors}
            showErrorList={false}
            validator={validator}
            customValidate={validate}>
            {showWaitlistHint && (
              <div className={"alert alert-warning waitlist-hint"}>
                {options?.waitlistHint || DEFAULT_WAITLIST_HINT}
                {waitlistedOffers.length > 0 && (
                  <div className={"mt-1 fw-semibold"}>Warteliste: {waitlistedOffers.join(", ")}</div>
                )}
              </div>
            )}
            <button className="btn btn-primary submit-button" type="submit">
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span className="visually-hidden">Loading...</span>
                </>
              ) : (
                "Anmelden"
              )}
            </button>
          </Form>
          {successMessage && <div className={"pt-2 success-message"}>{successMessage}</div>}
        </WaitlistContext.Provider>
      </LimitContext.Provider>
    </div>
  );

  function validate(formData: any, errors: FormValidation): FormValidation {
    const maxOffersToSelect = schema?.options?.maxOffersToSelect;
    if (!maxOffersToSelect || maxOffersToSelect <= 0) {
      return errors;
    }

    const excluded = schema?.options?.waitlistExcludedFromMaxOffers ? waitlistedOffers : [];
    const numberOfCheckedLimits = limits.filter(
      (limit) => formData[limit.property] === true && !excluded.includes(limit.property)
    ).length;

    if (numberOfCheckedLimits > maxOffersToSelect) {
      errors.addError(`Es können maximal ${maxOffersToSelect} Angebote ausgewählt werden.`);
    }

    return errors;
  }

  async function fetchLimitsOrSetError(): Promise<void> {
    const response = await fetchLimits(limitsEndpoint);

    if (response.limits && !response.errors) {
      setLimits(response.limits);
      return;
    }

    if (response.errors) {
      setExtraErrors(response.errors);
      return;
    }

    setUnexpectedError();
  }

  async function fetchSchemaOrSetError(): Promise<void> {
    const response = await fetchSchema(schemaEndpoint);

    if (response.schema && !response.errors) {
      const schema = response.schema;
      setSchema(schema);
      return;
    }

    if (response.errors) {
      setExtraErrors(response.errors);
      setSchema(undefined);
      return;
    }

    setUnexpectedError();
    setSchema(undefined);
  }

  async function submitForm(data: any): Promise<void> {
    setIsLoading(true);
    const response = await postRegistration(registrationEndpoint, data);

    if (!response.errors) {
      setSuccessMessage(toSuccessMessage(response.result));
      setFormData({});
      fetchLimitsOrSetError();
      setIsLoading(false);
      return;
    }

    fetchLimitsOrSetError();
    setSuccessMessage("");
    setExtraErrors(response.errors);
    setIsLoading(false);
  }

  function setUnexpectedError(): void {
    setExtraErrors({
      __errors: ["Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es erneut."]
    } as ErrorSchema);
  }
};
